import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    InputSignal,
    output,
    Signal,
} from '@angular/core';

import { HomeService } from '@connect-services/client/services/home.service';
import { CheckboxComponent, CheckboxState } from '@connect-services/src/lib/components/checkbox/checkbox.component';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { OrderEnum } from '@shared/client/models/pagination.model';

export type CustomColumn = {
    key: string;
    label: string;
    sortable?: boolean;
    textPosition?: 'justify-center' | 'justify-end' | 'justify-start';
    tooltip?: string;
    translate?: boolean;
};

export type SortState = {
    order?: OrderEnum;
    orderBy: string;
};

export interface RecordDataKey {
    [key: string]: unknown;
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CheckboxComponent, RessourcesModule],
    selector: 'app-custom-table',
    styleUrl: './table.component.scss',
    templateUrl: './table.component.html',
})
export class CustomTableComponent {
    public readonly columns = input.required<CustomColumn[]>();
    public readonly headerCheckboxState = input<CheckboxState>();
    public readonly isSelectable = input<boolean>();
    public readonly translateColumns: InputSignal<boolean> = input<boolean>(true);

    protected readonly homeService = inject(HomeService);
    protected readonly normalizedColumns: Signal<CustomColumn[]> = computed((): CustomColumn[] =>
        this.columns().map((column: CustomColumn): CustomColumn => ({
            ...column,
            sortable: column.sortable === undefined ? true : column.sortable,
            translate: column.translate || this.translateColumns(),
        })),
    );
    protected readonly OrderEnum = OrderEnum;
    protected sortEvent = output<SortState | undefined>();
    protected sortState?: SortState;
    protected toggleAllSelectedEvent = output<void>();

    public sort(column: CustomColumn): void {
        if (column.sortable === false) {
            return;
        }

        if (this.sortState?.orderBy !== column.key) {
            this.sortState = { order: OrderEnum.ASC, orderBy: column.key };
            this.sortEvent.emit(this.sortState);

            return;
        }

        if (this.sortState.order === OrderEnum.ASC) {
            this.sortState = { order: OrderEnum.DESC, orderBy: column.key };
            this.sortEvent.emit(this.sortState);

            return;
        }

        this.sortState = undefined;
        this.sortEvent.emit(this.sortState);
    }
}
